import React, { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CrossPlatformDashboard({ token }) {
  const [fbStatus, setFbStatus] = useState(null);
  const [gadsStatus, setGadsStatus] = useState(null);
  const [fbCampaigns, setFbCampaigns] = useState([]);
  const [gadsCampaigns, setGadsCampaigns] = useState([]);
  const [fbAnalytics, setFbAnalytics] = useState(null);
  const [gadsPerformance, setGadsPerformance] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = useCallback(() => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  }), [token]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [fbSt, gadsSt, fbCamp, gadsCamp, fbAn, gadsPerf] = await Promise.all([
        fetch(`${API_URL}/api/campaign/facebook-ads/status`, { headers: headers() }).then(r => r.json()).catch(() => null),
        fetch(`${API_URL}/api/campaign/google-ads/status`).then(r => r.json()).catch(() => null),
        fetch(`${API_URL}/api/campaign/facebook-ads/campaigns`, { headers: headers() }).then(r => r.json()).catch(() => ({ campaigns: [] })),
        fetch(`${API_URL}/api/campaign/google-ads/campaigns`, { headers: headers() }).then(r => r.json()).catch(() => ({ campaigns: [] })),
        fetch(`${API_URL}/api/campaign/facebook-ads/account-analytics`, { headers: headers() }).then(r => r.json()).catch(() => null),
        fetch(`${API_URL}/api/campaign/google-ads/performance?days=30`, { headers: headers() }).then(r => r.json()).catch(() => null),
      ]);
      setFbStatus(fbSt);
      setGadsStatus(gadsSt);
      setFbCampaigns(fbCamp?.campaigns || []);
      setGadsCampaigns(gadsCamp?.campaigns || []);
      setFbAnalytics(fbAn?.metrics || null);
      setGadsPerformance(gadsPerf?.overview || null);
    } catch (err) {
      console.error('Cross-platform load error:', err);
    }
    setLoading(false);
  }, [headers]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const fb = {
    impressions: fbAnalytics?.impressions || 0,
    clicks: fbAnalytics?.clicks || 0,
    spend: parseFloat(fbAnalytics?.spend || 0),
    reach: fbAnalytics?.reach || 0,
  };
  const gads = {
    impressions: gadsPerformance?.total_impressions || 0,
    clicks: gadsPerformance?.total_clicks || 0,
    spend: gadsPerformance?.total_spend || 0,
    conversions: gadsPerformance?.total_conversions || 0,
  };
  const combined = {
    impressions: fb.impressions + gads.impressions,
    clicks: fb.clicks + gads.clicks,
    spend: fb.spend + gads.spend,
    campaigns: fbCampaigns.length + gadsCampaigns.length,
    activeFb: fbCampaigns.filter(c => c.status === 'ACTIVE').length,
    activeGads: gadsCampaigns.filter(c => c.status === 'ACTIVE' || c.status === 'ENABLED').length,
  };
  const ctr = combined.impressions > 0 ? ((combined.clicks / combined.impressions) * 100).toFixed(2) : '0.00';
  const cpc = combined.clicks > 0 ? (combined.spend / combined.clicks).toFixed(2) : '0.00';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48" data-testid="cross-platform-loading">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-3 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-gray-500">Cross-Platform data load हो रहा है...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cross-platform-dashboard">
      {/* Platform Connection Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Facebook */}
        <div className={`rounded-xl border-2 p-4 flex items-center gap-4 ${fbStatus?.connected ? 'border-emerald-200 bg-emerald-50/50' : 'border-gray-200 bg-gray-50'}`} data-testid="fb-platform-card">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${fbStatus?.connected ? 'bg-[#1877F2]' : 'bg-gray-300'}`}>
            <i className="fa-brands fa-facebook-f text-white text-xl"></i>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h4 className="font-bold text-gray-800">Facebook Ads</h4>
              {fbStatus?.connected ? (
                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-semibold rounded-full"><i className="fa-solid fa-circle-check mr-1"></i>LIVE</span>
              ) : (
                <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs font-semibold rounded-full">Offline</span>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-0.5">{fbStatus?.account_name || '-'} &middot; {fbCampaigns.length} campaigns</p>
          </div>
          <div className="text-right">
            <p className="text-lg font-bold text-gray-700">₹{fb.spend.toLocaleString('en-IN')}</p>
            <p className="text-xs text-gray-400">Spend</p>
          </div>
        </div>

        {/* Google */}
        <div className={`rounded-xl border-2 p-4 flex items-center gap-4 ${gadsStatus?.connected ? 'border-emerald-200 bg-emerald-50/50' : 'border-amber-200 bg-amber-50/50'}`} data-testid="gads-platform-card">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${gadsStatus?.connected ? 'bg-[#4285F4]' : 'bg-amber-400'}`}>
            <i className="fa-brands fa-google text-white text-xl"></i>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h4 className="font-bold text-gray-800">Google Ads</h4>
              {gadsStatus?.connected ? (
                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-semibold rounded-full"><i className="fa-solid fa-circle-check mr-1"></i>LIVE</span>
              ) : (
                <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full"><i className="fa-solid fa-clock mr-1"></i>{gadsStatus?.access_level || 'Pending'}</span>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-0.5">{gadsStatus?.account_name || 'Test Account'} &middot; {gadsCampaigns.length} campaigns</p>
          </div>
          <div className="text-right">
            <p className="text-lg font-bold text-gray-700">₹{gads.spend.toLocaleString('en-IN')}</p>
            <p className="text-xs text-gray-400">Spend</p>
          </div>
        </div>
      </div>

      {/* Combined Metrics */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-6 text-white" data-testid="combined-metrics">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-lg font-bold">Combined Performance</h3>
            <p className="text-sm text-white/60">Facebook + Google — Last 30 Days</p>
          </div>
          <button onClick={loadAll} className="p-2 hover:bg-white/10 rounded-lg transition" data-testid="cross-refresh-btn">
            <i className="fa-solid fa-arrows-rotate text-white/60"></i>
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {[
            { label: 'Total Spend', value: `₹${combined.spend.toLocaleString('en-IN')}`, sub: 'FB + Google combined', icon: 'fa-indian-rupee-sign', accent: 'bg-emerald-500' },
            { label: 'Total Campaigns', value: combined.campaigns, sub: `${combined.activeFb + combined.activeGads} active`, icon: 'fa-rectangle-ad', accent: 'bg-blue-500' },
            { label: 'Impressions', value: combined.impressions.toLocaleString(), sub: 'Total ad views', icon: 'fa-eye', accent: 'bg-violet-500' },
            { label: 'Clicks', value: combined.clicks.toLocaleString(), sub: 'Total clicks', icon: 'fa-arrow-pointer', accent: 'bg-amber-500' },
            { label: 'Avg CTR', value: `${ctr}%`, sub: 'Click-through rate', icon: 'fa-chart-line', accent: 'bg-rose-500' },
            { label: 'Avg CPC', value: `₹${cpc}`, sub: 'Cost per click', icon: 'fa-coins', accent: 'bg-cyan-500' },
          ].map((m, i) => (
            <div key={i} className="bg-white/5 rounded-xl p-4 border border-white/10 hover:bg-white/10 transition">
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-7 h-7 rounded-lg ${m.accent} flex items-center justify-center`}>
                  <i className={`fa-solid ${m.icon} text-white text-xs`}></i>
                </div>
                <p className="text-xs text-white/60">{m.label}</p>
              </div>
              <p className="text-xl font-bold">{m.value}</p>
              <p className="text-xs text-white/40 mt-1">{m.sub}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Platform Comparison */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* FB vs Google — Visual Bars */}
        <div className="bg-white rounded-xl border p-5" data-testid="platform-comparison">
          <h4 className="font-bold text-gray-700 mb-4 flex items-center gap-2">
            <i className="fa-solid fa-chart-bar text-orange-500"></i> Platform Comparison
          </h4>
          <div className="space-y-4">
            {[
              { label: 'Spend', fb: fb.spend, gads: gads.spend, format: (v) => `₹${v.toLocaleString('en-IN')}` },
              { label: 'Impressions', fb: fb.impressions, gads: gads.impressions, format: (v) => v.toLocaleString() },
              { label: 'Clicks', fb: fb.clicks, gads: gads.clicks, format: (v) => v.toLocaleString() },
              { label: 'Campaigns', fb: fbCampaigns.length, gads: gadsCampaigns.length, format: (v) => v },
            ].map((item, i) => {
              const total = item.fb + item.gads;
              const fbPct = total > 0 ? (item.fb / total * 100) : 50;
              const gadsPct = total > 0 ? (item.gads / total * 100) : 50;
              return (
                <div key={i}>
                  <div className="flex items-center justify-between text-sm mb-1.5">
                    <span className="text-gray-600 font-medium">{item.label}</span>
                    <span className="text-gray-400 text-xs">{item.format(total)} total</span>
                  </div>
                  <div className="flex h-7 rounded-lg overflow-hidden bg-gray-100">
                    <div className="bg-[#1877F2] flex items-center justify-center transition-all duration-500" style={{ width: `${Math.max(fbPct, 5)}%` }}>
                      <span className="text-[10px] text-white font-semibold">{item.format(item.fb)}</span>
                    </div>
                    <div className="bg-[#4285F4] flex items-center justify-center transition-all duration-500" style={{ width: `${Math.max(gadsPct, 5)}%` }}>
                      <span className="text-[10px] text-white font-semibold">{item.format(item.gads)}</span>
                    </div>
                  </div>
                  <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                    <span><i className="fa-brands fa-facebook text-[#1877F2] mr-1"></i>Facebook ({fbPct.toFixed(0)}%)</span>
                    <span>Google ({gadsPct.toFixed(0)}%)<i className="fa-brands fa-google text-[#4285F4] ml-1"></i></span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* All Campaigns Combined */}
        <div className="bg-white rounded-xl border p-5" data-testid="all-campaigns-list">
          <h4 className="font-bold text-gray-700 mb-4 flex items-center gap-2">
            <i className="fa-solid fa-list text-orange-500"></i> All Campaigns ({combined.campaigns})
          </h4>
          <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
            {[
              ...fbCampaigns.map(c => ({ ...c, platform: 'facebook', platformIcon: 'fa-brands fa-facebook', platformColor: '#1877F2' })),
              ...gadsCampaigns.map(c => ({
                id: c.campaign_id,
                name: c.campaign_name || c.name,
                status: c.status,
                objective: c.campaign_type,
                daily_budget: c.daily_budget ? c.daily_budget * 100 : 0,
                platform: 'google',
                platformIcon: 'fa-brands fa-google',
                platformColor: '#4285F4',
              })),
            ].sort((a, b) => (a.status === 'ACTIVE' ? -1 : 1) - (b.status === 'ACTIVE' ? -1 : 1)).map((camp, i) => (
              <div key={`${camp.platform}-${camp.id}-${i}`} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-gray-50 border border-transparent hover:border-gray-200 transition">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: camp.platformColor }}>
                  <i className={`${camp.platformIcon} text-white text-sm`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-700 truncate">{camp.name}</p>
                  <p className="text-xs text-gray-400">
                    {camp.objective?.replace('OUTCOME_', '') || camp.campaign_type || '-'}
                    {camp.daily_budget ? ` · ₹${(parseInt(camp.daily_budget) / 100).toLocaleString()}/day` : ''}
                  </p>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${
                  camp.status === 'ACTIVE' || camp.status === 'ENABLED' ? 'bg-emerald-100 text-emerald-700 border-emerald-300' :
                  camp.status === 'PAUSED' ? 'bg-amber-100 text-amber-700 border-amber-300' :
                  'bg-gray-100 text-gray-500 border-gray-300'
                }`}>
                  {camp.status === 'ACTIVE' || camp.status === 'ENABLED' ? <i className="fa-solid fa-circle text-[5px] mr-1 animate-pulse"></i> : null}
                  {camp.status}
                </span>
              </div>
            ))}
            {combined.campaigns === 0 && (
              <div className="text-center py-8">
                <i className="fa-solid fa-rectangle-ad text-3xl text-gray-300 mb-2"></i>
                <p className="text-sm text-gray-400">अभी कोई campaign नहीं है</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Platform-wise Spend Breakdown */}
      <div className="bg-white rounded-xl border p-5" data-testid="spend-breakdown">
        <h4 className="font-bold text-gray-700 mb-4 flex items-center gap-2">
          <i className="fa-solid fa-chart-pie text-orange-500"></i> Budget Allocation
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-[#1877F2]/10 to-[#1877F2]/5 rounded-xl p-4 border border-[#1877F2]/20">
            <div className="flex items-center gap-2 mb-1.5">
              <i className="fa-brands fa-facebook text-[#1877F2]"></i>
              <span className="text-sm font-semibold text-gray-700">FB Spend</span>
            </div>
            <p className="text-2xl font-bold text-[#1877F2]">₹{fb.spend.toLocaleString('en-IN')}</p>
            <p className="text-xs text-gray-400 mt-1">{fbCampaigns.length} campaigns</p>
          </div>
          <div className="bg-gradient-to-br from-[#4285F4]/10 to-[#4285F4]/5 rounded-xl p-4 border border-[#4285F4]/20">
            <div className="flex items-center gap-2 mb-1.5">
              <i className="fa-brands fa-google text-[#4285F4]"></i>
              <span className="text-sm font-semibold text-gray-700">Google Spend</span>
            </div>
            <p className="text-2xl font-bold text-[#4285F4]">₹{gads.spend.toLocaleString('en-IN')}</p>
            <p className="text-xs text-gray-400 mt-1">{gadsCampaigns.length} campaigns</p>
          </div>
          <div className="bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 rounded-xl p-4 border border-emerald-200">
            <div className="flex items-center gap-2 mb-1.5">
              <i className="fa-solid fa-chart-line text-emerald-600"></i>
              <span className="text-sm font-semibold text-gray-700">Total Budget</span>
            </div>
            <p className="text-2xl font-bold text-emerald-600">₹{combined.spend.toLocaleString('en-IN')}</p>
            <p className="text-xs text-gray-400 mt-1">30 days combined</p>
          </div>
          <div className="bg-gradient-to-br from-orange-500/10 to-orange-500/5 rounded-xl p-4 border border-orange-200">
            <div className="flex items-center gap-2 mb-1.5">
              <i className="fa-solid fa-bullseye text-orange-600"></i>
              <span className="text-sm font-semibold text-gray-700">Active Campaigns</span>
            </div>
            <p className="text-2xl font-bold text-orange-600">{combined.activeFb + combined.activeGads}</p>
            <p className="text-xs text-gray-400 mt-1">FB: {combined.activeFb} · Google: {combined.activeGads}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
